import { useState, useEffect } from 'react';
import api from '../services/api';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const PALETTE = ['#b3322a', '#2563eb', '#2e9e4f', '#8e44ad', '#d97706', '#0e7490', '#be185d', '#4d7c0f'];

function colorFor(name) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % PALETTE.length;
  return PALETTE[h];
}

function ymd(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Read a stored calendar date as YYYY-MM-DD without any timezone shift.
// The API returns DATE values as UTC-midnight ISO strings; taking the date
// portion directly avoids new Date() pulling it back a day in local time.
function dateKey(d) {
  if (!d) return '';
  const s = typeof d === 'string' ? d : new Date(d).toISOString();
  return s.slice(0, 10);
}

export default function PTOCalendar({ celebrations = [] }) {
  const [pto, setPto] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });

  useEffect(() => {
    api.get('/breakroom/pto').then(({ data }) => setPto(data)).catch(() => {});
  }, []);

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const today = ymd(new Date());

  // Normalize leaves and assign non-overlapping lanes (greedy)
  const leaves = pto
    .map((p) => ({
      name: (p.name || '').split(' ')[0],
      fullName: p.name,
      color: colorFor(p.name || '?'),
      start: dateKey(p.start_date),
      end: dateKey(p.end_date),
    }))
    .sort((a, b) => a.start.localeCompare(b.start));

  const laneEnds = []; // laneIndex -> last end ymd
  leaves.forEach((lv) => {
    let lane = laneEnds.findIndex((end) => end < lv.start);
    if (lane === -1) { lane = laneEnds.length; laneEnds.push(lv.end); }
    else laneEnds[lane] = lv.end;
    lv.lane = lane;
  });
  const laneCount = Math.max(laneEnds.length, 0);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);

  return (
    <div style={{ fontFamily: "'Georgia', serif" }}>
      <div className="flex items-center justify-between px-3 py-2 rounded-t" style={{ backgroundColor: '#b3322a' }}>
        <button onClick={() => setCursor(new Date(year, month - 1, 1))} className="text-white/80 hover:text-white px-2 text-lg leading-none">‹</button>
        <h3 className="text-white font-black tracking-wide uppercase text-sm">{MONTHS[month]} {year}</h3>
        <button onClick={() => setCursor(new Date(year, month + 1, 1))} className="text-white/80 hover:text-white px-2 text-lg leading-none">›</button>
      </div>

      <div className="grid grid-cols-7" style={{ backgroundColor: '#fdfaf2', padding: '6px 6px 10px' }}>
        {DOW.map((d) => (
          <div key={d} className="text-center text-[10px] font-bold uppercase tracking-wide pb-1" style={{ color: '#9a3b32' }}>{d}</div>
        ))}
        {cells.map((day, i) => {
          if (!day) return <div key={i} style={{ minHeight: 60 }} />;
          const cellDate = new Date(year, month, day);
          const key = ymd(cellDate);
          const dow = cellDate.getDay();
          const isToday = key === today;

          const laneRows = [];
          for (let li = 0; li < laneCount; li++) {
            laneRows.push(leaves.find((l) => l.lane === li && key >= l.start && key <= l.end) || null);
          }
          const dayCelebs = celebrations.filter((c) => c.event_month === month + 1 && c.event_day === day);

          return (
            <div key={i} style={{ minHeight: 60, border: '1px solid #e6ddc6', boxShadow: isToday ? 'inset 0 0 0 2px #b3322a' : 'none', position: 'relative' }}>
              <div className="text-[11px] font-bold px-1 pt-0.5 flex items-center justify-between" style={{ color: isToday ? '#b3322a' : '#5a4a32' }}>
                <span>{day}</span>
                {dayCelebs.length > 0 && (
                  <span className="pr-1 leading-none">
                    {dayCelebs.map((c, ci) => (
                      <span key={ci} title={`${c.person_name} — ${c.type === 'birthday' ? 'Birthday' : 'Work Anniversary'}`}>{c.type === 'birthday' ? '🎂' : '🎉'}</span>
                    ))}
                  </span>
                )}
              </div>
              <div className="mt-0.5" style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {laneRows.map((lv, li) => {
                  if (!lv) return <div key={li} style={{ height: 15 }} />;
                  const isStart = key === lv.start;
                  const isEnd = key === lv.end;
                  // repeat the name at the start, on week wrap (Sunday), and at the month's first day
                  const showName = isStart || dow === 0 || day === 1;
                  return (
                    <div
                      key={li}
                      title={`${lv.fullName} — Out of Office`}
                      style={{
                        height: 15,
                        backgroundColor: lv.color,
                        borderTopLeftRadius: isStart ? 7 : 0,
                        borderBottomLeftRadius: isStart ? 7 : 0,
                        borderTopRightRadius: isEnd ? 7 : 0,
                        borderBottomRightRadius: isEnd ? 7 : 0,
                        display: 'flex',
                        alignItems: 'center',
                        overflow: 'hidden',
                      }}
                    >
                      {showName && (
                        <span style={{ color: '#fff', fontSize: 9.5, fontWeight: 700, paddingLeft: 5, whiteSpace: 'nowrap', textShadow: '0 1px 1px rgba(0,0,0,0.25)' }}>
                          {lv.name}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {pto.length === 0 && (
        <p className="text-xs text-center py-2 italic rounded-b" style={{ backgroundColor: '#fdfaf2', color: '#9a8a6a' }}>
          Nobody's escaped yet. Be the first. 🏃
        </p>
      )}
    </div>
  );
}
